import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Home, Calendar } from 'lucide-react';
import { CalendarScheduler } from './calendar-scheduler';
import { Logo } from './Logo';
import { GlowingEffect } from './GlowingEffect';

interface BookingPageProps {
  onBack: () => void;
  onLogin?: () => void;
  onBookCall?: (data: { name: string; email: string; date: string }) => void;
}

/**
 * Re-implementing NavButton from LetsWorkTogether to maintain visual consistency
 * across the platform's top-level navigation areas.
 */
function NavButton({ label, onClick, variant = "default", className = "" }: { label: string; onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void; variant?: "default" | "primary" | "outline"; className?: string }) {
  return (
    <div className={`relative rounded-full ${className} transform-gpu`}>
      <GlowingEffect
        spread={25}
        glow={true}
        disabled={false}
        proximity={50}
        inactiveZone={0.01}
        borderWidth={1.5}
      />
      <button 
        onClick={onClick}
        className={`relative flex items-center justify-center rounded-full border border-border/50 px-6 py-2.5 backdrop-blur-md transition-all group outline-none focus:ring-1 focus:ring-foreground/20 ${
          variant === "primary" 
            ? "bg-primary text-primary-foreground hover:bg-primary/90" 
            : variant === "outline"
            ? "bg-background/20 text-foreground border-foreground/20 hover:bg-background/40"
            : "bg-background/50 text-muted-foreground hover:bg-muted/50 hover:text-foreground"
        }`}
      >
        <span className="text-[10px] font-bold tracking-[0.3em] uppercase transition-colors">
          {label}
        </span>
      </button>
    </div>
  );
}

export function BookingPage({ onBack, onLogin, onBookCall }: BookingPageProps) {
  const [isBooked, setIsBooked] = useState(false);
  const [bookingData, setBookingData] = useState<{ name: string; date: string } | null>(null);

  const handleBookingConfirm = (data: { name: string; email: string; date: string }) => {
    setBookingData({ name: data.name, date: data.date });
    setIsBooked(true);
    if (onBookCall) {
      onBookCall(data);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }}
      className="bg-black min-h-screen flex flex-col relative overflow-hidden"
    >
      {/* Signature background glows for Aure OS aesthetic */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full pointer-events-none transform-gpu">
        <div className="absolute top-[-10%] right-[10%] w-[800px] h-[800px] bg-primary/10 blur-[150px] rounded-full opacity-50" />
        <div className="absolute bottom-[-10%] left-[10%] w-[800px] h-[800px] bg-primary/5 blur-[150px] rounded-full opacity-30" />
      </div>

      {/* Header Layer - Full width header for visual consistency with the landing page */}
      <AnimatePresence>
        {!isBooked && (
          <motion.header 
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            className="w-full max-w-7xl mx-auto flex items-center justify-between py-10 px-6 z-50 transform-gpu"
          >
            <Logo />
            <div className="flex items-center gap-4">
              <NavButton label="Back to Home" variant="outline" onClick={onBack} />
              <NavButton label="Login" variant="primary" onClick={onLogin} />
            </div>
          </motion.header>
        )}
      </AnimatePresence>

      <div className="flex-1 w-full max-w-[1200px] mx-auto px-6 pb-20 flex flex-col items-center justify-center z-10">
        <AnimatePresence mode="wait">
          {!isBooked ? (
            <motion.div
              key="scheduler"
              initial={{ opacity: 0, scale: 0.98, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="w-full h-full"
            >
              <CalendarScheduler 
                id="booking-page-focused-scheduler" 
                minimal={true} 
                onConfirm={handleBookingConfirm} 
              />
            </motion.div>
          ) : (
            <motion.div
              key="success-screen"
              initial={{ opacity: 0, scale: 0.9, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="w-full max-w-xl text-center space-y-12 p-12 rounded-[64px] border border-white/5 bg-white/[0.02] backdrop-blur-3xl shadow-2xl relative overflow-hidden"
            >
              {/* Internal glow for success card */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-emerald-500/10 blur-[80px] rounded-full pointer-events-none" />

              <div className="space-y-6 relative z-10">
                <div className="flex justify-center">
                  <div className="size-24 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-500 shadow-[0_0_50px_rgba(16,185,129,0.1)]">
                    <CheckCircle2 size={48} strokeWidth={1.5} />
                  </div>
                </div>
                <div className="space-y-2">
                  <h2 className="text-4xl font-light tracking-tight">Booking <span className="italic">Confirmed</span></h2>
                  <p className="text-muted-foreground font-light max-w-xs mx-auto leading-relaxed">
                    Thank you, {bookingData?.name}. Your session has been successfully added to our schedule.
                  </p>
                </div>
              </div>

              <div className="p-8 rounded-[32px] border border-white/5 bg-white/[0.01] space-y-4">
                <div className="flex items-center justify-center gap-4 text-muted-foreground/60">
                   <Calendar size={18} strokeWidth={1.5} />
                   <span className="text-[11px] font-bold tracking-[0.2em] uppercase">{bookingData?.date}</span>
                </div>
                <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-primary/60">Strategy Session Verified</p>
              </div>

              <motion.button
                whileHover={{ scale: 0.98 }}
                whileTap={{ scale: 0.95 }}
                onClick={onBack}
                className="w-full group relative flex items-center justify-center gap-4 py-5 rounded-full bg-primary text-primary-foreground text-[11px] font-bold tracking-[0.5em] uppercase shadow-2xl shadow-primary/20 transition-all overflow-hidden"
              >
                <div className="relative z-10 flex items-center gap-3">
                   <Home size={16} />
                   Return to Home
                </div>
                {/* Subtle shine effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              </motion.button>

              <div className="text-[8px] tracking-[0.4em] uppercase text-muted-foreground/20 font-bold">
                A verification email has been dispatched
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
